/**
 * Library (PRD-1605): one collection of documents in three types. A document
 * has a name, a type, a file and an upload state; nothing is parsed, so
 * Available is terminal — the file is stored.
 *
 * There is no Library API yet, so the model holds the documents itself,
 * seeded locally. It is created once in App.js and lives for the session,
 * which is what lets the page restore the tab, filters, view and search a
 * user left when they come back to it.
 */

export const LIBRARY_TYPES = {
  reference_key: {
    label: 'Reference key',
    plural: 'Reference keys',
    one: 'reference key',
    many: 'reference keys',
    icon: 'grid-four',
    hint: 'The command rubric the output is marked against.',
  },
  sop: {
    label: 'SOP',
    plural: 'SOPs',
    one: 'SOP',
    many: 'SOPs',
    icon: 'book-open',
    hint: "The instructor's run book. Context only, not scored.",
  },
  narrative: {
    label: 'Narrative',
    plural: 'Narratives',
    one: 'narrative',
    many: 'narratives',
    icon: 'file-text',
    hint: 'The scenario story given to the crew.',
  },
};

export const LIBRARY_TYPE_ORDER = ['reference_key', 'sop', 'narrative'];

export const DEFAULT_LIBRARY_FILTERS = {
  visibility: 'active',
  usage: 'all',
  sort: 'date_desc',
  search: '',
};

const HOUR = 3600000;
const DAY = 86400000;

/**
 * Dated from load so every rung of the relative-date ladder shows: minutes,
 * hours, yesterday, a weekday, and one beyond a week. One archived and one
 * failed, so both states are reachable without waiting. Names are invented.
 */
export function seedLibraryDocuments(now = Date.now()) {
  const ago = (ms) => new Date(now - ms).toISOString();
  return [
    { id: 'd1', type: 'reference_key', name: 'Bridge Command Reference Key', fileName: 'Bridge_Command_Reference_Key_v3.xlsx', uploadedAt: ago(9 * DAY), status: 'available', usedBy: 0 },
    { id: 'd2', type: 'reference_key', name: 'Harbour Pilotage Reference Key', fileName: 'Harbour_Pilotage_Reference_Key.xlsx', uploadedAt: ago(4 * DAY), status: 'available', usedBy: 0 },
    { id: 'd3', type: 'reference_key', name: 'Towage Reference Key', fileName: 'Towage_Reference_Key_draft.csv', uploadedAt: ago(2 * HOUR), status: 'failed', usedBy: 0 },
    { id: 'd4', type: 'sop', name: 'Simulator Run Book', fileName: 'Bridge_Simulator_SOP.pdf', uploadedAt: ago(9 * DAY), status: 'available', usedBy: 0 },
    { id: 'd5', type: 'sop', name: 'Night Order Book', fileName: 'Night_Order_Book_2026.pdf', uploadedAt: ago(DAY + 3 * HOUR), status: 'available', usedBy: 0 },
    { id: 'd6', type: 'sop', name: 'Legacy Watchkeeping SOP', fileName: 'Watchkeeping_SOP_2019.pdf', uploadedAt: ago(26 * DAY), status: 'available', archived: true, usedBy: 0 },
    { id: 'd7', type: 'narrative', name: 'Fishing Fleet Crossing', fileName: 'Narrative_Fishing_Fleet_Crossing.pdf', uploadedAt: ago(4 * DAY), status: 'available', usedBy: 0 },
    { id: 'd8', type: 'narrative', name: 'Fog Bank on Final Approach', fileName: 'Narrative_Fog_Bank_Approach.pdf', uploadedAt: ago(DAY + 5 * HOUR), status: 'available', usedBy: 0 },
    { id: 'd9', type: 'narrative', name: 'Crew Overboard, Port Quarter', fileName: 'Narrative_Crew_Overboard.pdf', uploadedAt: ago(18 * 60000), status: 'available', usedBy: 0 },
  ];
}

/**
 * Scenarios belong to Assessments, which has no way to create one yet, so
 * every seeded document is at zero. When bindings arrive this should read the
 * scenarios pointing at the document; nothing else needs to change.
 */
export function usedBy(doc) {
  return doc.usedBy || 0;
}

/** "1 scenario" / "2 scenarios": the one place the Library picks singular or plural. */
export function plural(n, one, many) {
  return n === 1 ? one : many;
}

/**
 * Why a document can't be archived, as the toast says it, or null (PRD-1607).
 * A document a scenario still points at refuses rather than guessing at a
 * cascade, because binding lifecycle is an open question in the handoff.
 */
export function archiveProblem(doc) {
  const n = usedBy(doc);
  return n ? `In use by ${n} ${plural(n, 'scenario', 'scenarios')}. Change the scenario first.` : null;
}

function inVisibility(doc, visibility) {
  if (visibility === 'archived') return !!doc.archived;
  if (visibility === 'all') return true;
  return !doc.archived;
}

/* ---- Files (PRD-1606) ----------------------------------------------------
   Nothing is parsed or validated structurally: the extension is checked and the
   size capped, and that is all. */
export const LIBRARY_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
export const LIBRARY_FILE_ACCEPT = LIBRARY_FILE_EXTENSIONS.map((e) => `.${e}`).join(',');
export const LIBRARY_FILE_LABEL = 'PDF, Word, Excel or CSV';
export const LIBRARY_MAX_FILE_MB = 100;

/** Why a file can't be added, as the toast says it, or null when it can. */
export function libraryFileProblem(file) {
  const ext = (String(file.name).split('.').pop() || '').toLowerCase();
  if (!LIBRARY_FILE_EXTENSIONS.includes(ext)) return `Not a document. Add a ${LIBRARY_FILE_LABEL} file.`;
  if (file.size > LIBRARY_MAX_FILE_MB * 1024 * 1024) return `That file is over ${LIBRARY_MAX_FILE_MB}mb.`;
  return null;
}

/** Sizes read as 412kb and 1.8mb: lower case, no space. */
export function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}mb`;
}

const EMPTY_DRAFT = { name: '', type: null, file: null };
const UPLOAD_TICK_MS = 700;

/** Documents of the open type that pass the filters and search, sorted. */
export function filterLibraryDocuments(documents, type, filters) {
  const term = filters.search.trim().toLowerCase();
  const out = documents.filter((d) => {
    if (d.type !== type || !inVisibility(d, filters.visibility)) return false;
    if (filters.usage === 'in' && usedBy(d) === 0) return false;
    if (filters.usage === 'out' && usedBy(d) > 0) return false;
    if (!term) return true;
    return d.name.toLowerCase().includes(term) || d.fileName.toLowerCase().includes(term);
  });
  const byName = (a, b) => a.name.localeCompare(b.name);
  if (filters.sort === 'name_asc') return out.sort(byName);
  if (filters.sort === 'name_desc') return out.sort((a, b) => byName(b, a));
  if (filters.sort === 'date_asc') return out.sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
  return out.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}

/** The "of N" in the count line: the open type within the visibility scope. */
export function libraryScopeTotal(documents, type, visibility) {
  return documents.filter((d) => d.type === type && inVisibility(d, visibility)).length;
}

export class LibraryModel {
  /**
   * `random` and `tickMs` only exist so tests can drive the upload engine
   * deterministically; the app takes the defaults.
   */
  constructor(documents = seedLibraryDocuments(), { random = Math.random, tickMs = UPLOAD_TICK_MS } = {}) {
    this.documents = documents;
    this.type = LIBRARY_TYPE_ORDER[0];
    this.view = 'grid';
    this.filters = { ...DEFAULT_LIBRARY_FILTERS };
    this.draft = { ...EMPTY_DRAFT };
    this.listeners = [];
    this.random = random;
    this.tickMs = tickMs;
    this.uploadTimer = null;
    this.nextId = 1;
  }

  /** A tab changes the type and nothing else: filters, sort, view and search carry across. */
  setType(type) {
    if (!LIBRARY_TYPES[type] || type === this.type) return;
    this.type = type;
    this.notify();
  }

  setView(view) {
    if (view !== 'grid' && view !== 'list') return;
    this.view = view;
    this.notify();
  }

  setFilter(key, value) {
    if (!(key in this.filters)) return;
    this.filters = { ...this.filters, [key]: value };
    this.notify();
  }

  /** The no-result way out. The tab, sort and view are what the user chose to look at, so they stay. */
  clearSearchAndFilters() {
    this.filters = {
      ...this.filters,
      search: DEFAULT_LIBRARY_FILTERS.search,
      visibility: DEFAULT_LIBRARY_FILTERS.visibility,
      usage: DEFAULT_LIBRARY_FILTERS.usage,
    };
    this.notify();
  }

  /** A part-filled Add document form survives being dismissed and comes back on the next open. */
  setDraft(patch) {
    this.draft = { ...this.draft, ...patch };
    this.notify();
  }

  /**
   * Saves a document and starts its upload. It lands on its own type's tab,
   * whichever tab was open, and the draft clears. If the search or filters
   * carried over would hide it, they clear too, so the user always sees what
   * they just added climb; the sort and view stay.
   */
  addDocument({ name, type, file }) {
    if (!LIBRARY_TYPES[type] || !name.trim() || !file) return null;
    const doc = {
      id: `n${this.nextId++}`,
      type,
      name: name.trim(),
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      status: 'uploading',
      progress: 0,
      usedBy: 0,
    };
    this.documents = [doc, ...this.documents];
    this.type = type;
    this.draft = { ...EMPTY_DRAFT };
    if (!filterLibraryDocuments([doc], type, this.filters).length) {
      this.filters = {
        ...this.filters,
        search: DEFAULT_LIBRARY_FILTERS.search,
        visibility: DEFAULT_LIBRARY_FILTERS.visibility,
        usage: DEFAULT_LIBRARY_FILTERS.usage,
      };
    }
    this.notify();
    this.startUploads();
    return doc;
  }

  /** Restarts a failed transfer from zero. There is no resume (handoff open question 3). */
  retry(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || doc.status !== 'failed') return;
    this.updateDocument(id, { status: 'uploading', progress: 0 });
    this.startUploads();
  }

  /**
   * Objects are archived, never deleted (PRD-1607). An archived document keeps
   * its last upload state and is reachable under the Archived filter. Returns
   * whether it archived; a document in use by a scenario refuses, and so does
   * one still uploading (Uploading has no actions in the handoff).
   */
  archive(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || doc.archived || doc.status === 'uploading' || archiveProblem(doc)) return false;
    this.updateDocument(id, { archived: true });
    return true;
  }

  unarchive(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || !doc.archived) return false;
    this.updateDocument(id, { archived: false });
    return true;
  }

  /**
   * One interval for the whole page, stopped as soon as nothing is uploading.
   * The rate is a demo convenience, not a model of real transfer.
   */
  startUploads() {
    if (this.uploadTimer) return;
    this.uploadTimer = setInterval(() => this.tickUploads(), this.tickMs);
  }

  tickUploads() {
    if (!this.documents.some((d) => d.status === 'uploading' && !d.archived)) {
      this.stopUploads();
      return;
    }
    this.documents = this.documents.map((d) => {
      if (d.status !== 'uploading' || d.archived) return d;
      const progress = Math.min(100, (d.progress || 0) + 4 + this.random() * 6);
      return progress >= 100 ? { ...d, status: 'available', progress: 100 } : { ...d, progress };
    });
    this.notify();
  }

  stopUploads() {
    clearInterval(this.uploadTimer);
    this.uploadTimer = null;
  }

  updateDocument(id, patch) {
    this.documents = this.documents.map((d) => (d.id === id ? { ...d, ...patch } : d));
    this.notify();
  }

  getState() {
    return {
      documents: this.documents,
      type: this.type,
      view: this.view,
      filters: this.filters,
      draft: this.draft,
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
}
